/* global setTimeout, clearTimeout */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    ActivityIndicator,
    FlatList,
    Platform,
    RefreshControl,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {usePremiumLegacyTheme} from '../helpers/modeScreenTheme';
import {useManagementTheme} from '../helpers/managementTheme';
import {useWorkSheets} from '../helpers/WorkSheetContext';
import usePagedServerList from '../hooks/usePagedServerList';
import {
    byNewestFirst,
    countByFilter,
    fetchWorkOrderCounts,
    fetchWorkOrderPage,
    matchesFilter,
    WORK_ORDER_PAGE_SIZE,
} from '../helpers/workOrderListApi';
import {PillTabs} from '../components/premium';
import {BoboLoading} from '../components/bobo';
import NewWorkSheetModal from '../components/NewWorkSheetModal';
import {ServiceEmptyState} from '../components/ServiceUI';
import {useTopInset} from '../helpers/safeArea';

const STATUS_FILTERS = ['active', 'pending', 'in_progress', 'completed', 'invoiced', 'all'];

// The default "active" view hides finished (completed) and closed (invoiced) work orders;
// cancelled/deleted records are dropped upstream. Filtering, searching, sorting and paging
// are done by the backend (helpers/workOrderListApi.js) so thousands of imported work
// orders never have to be downloaded or scanned on the device.

/**
 * Work-sheet status as a tinted pill (tone colour on a 14 % tint).
 *
 * @param {object} props
 * @param {string} props.status `pending` | `in_progress` | `completed` | `invoiced`.
 * @param {Function} props.t i18n translate.
 * @returns {JSX.Element} The pill.
 */
const StatusBadge = ({ status, t }) => {
    const m = useManagementTheme();
    const config = {
        pending: { label: t('repair_status_pending'), tone: 'warning' },
        in_progress: { label: t('repair_status_in_progress'), tone: 'info' },
        completed: { label: t('repair_status_completed'), tone: 'success' },
        invoiced: { label: t('repair_status_invoiced'), tone: 'violet' },
    };
    const c = config[status] || config.pending;
    return (
        <View style={[styles.statusBadge, { backgroundColor: m.toneTint(c.tone, 0.14) }]}>
            <Text style={[styles.statusBadgeText, { color: m.toneColor(c.tone) }]}>{c.label}</Text>
        </View>
    );
};

/**
 * One work-order card. Memoised so appending a page / updating one row does not
 * re-render (and re-run the parts reduce for) every card already on screen.
 *
 * @param {object} props
 * @param {object} props.item Normalised work order.
 * @param {Function} props.onPress Called with the work order.
 * @param {object} props.theme Legacy premium theme.
 * @param {object} props.m Management theme (shadow / surface tokens).
 * @param {Function} props.t i18n translate.
 * @returns {JSX.Element} The card.
 */
const WorkSheetCard = React.memo(function WorkSheetCard({ item, onPress, theme, m, t }) {
    const partsList = Array.isArray(item.parts) ? item.parts : [];
    const tasksList = Array.isArray(item.tasks) ? item.tasks : [];
    const totalParts = partsList.reduce((sum, p) => sum + ((p?.qty || 0) * (p?.unitPrice || 0)), 0);
    const completedTasks = tasksList.filter(task => task.status === 'completed').length;

    return (
        <TouchableOpacity
            style={[styles.wsCard, {
                backgroundColor: theme.colors.card,
                borderColor: theme.colors.border,
            }, m.shadow(1)]}
            onPress={() => onPress(item)}
            activeOpacity={0.8}
        >
            <View style={styles.wsCardHeader}>
                <View style={styles.wsIdContainer}>
                    <Icon name="file-document-outline" size={16} color={theme.colors.primary} />
                    <Text style={[styles.wsId, { color: theme.colors.primary }]}>{item.worksheetId}</Text>
                </View>
                <StatusBadge status={item.status} t={t} />
            </View>

            <View style={styles.wsCardBody}>
                <Text style={[styles.wsCustomer, { color: theme.colors.text }]}>{item.customer}</Text>
                <View style={styles.wsCarRow}>
                    <Icon name="car" size={16} color={theme.colors.subText} />
                    <Text style={[styles.wsCarText, { color: theme.colors.subText }]}>{item.car} ({item.year})</Text>
                    <View style={[styles.wsPlateBadge, {
                        backgroundColor: m.colors.surfaceAlt,
                        borderColor: theme.colors.border,
                    }]}>
                        <Text style={[styles.wsPlateText, { color: theme.colors.text }]}>{item.plate}</Text>
                    </View>
                </View>
                <Text style={[styles.wsJobText, { color: theme.colors.subText }]} numberOfLines={1}>
                    <Icon name="wrench" size={13} color={theme.colors.subText} /> {item.jobSummary}
                </Text>
            </View>

            <View style={[styles.wsCardFooter, { borderTopColor: theme.colors.border }]}>
                <View style={styles.wsFooterItem}>
                    <Icon name="clock-outline" size={14} color={theme.colors.subText} />
                    <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>{item.estimatedHours}h</Text>
                </View>
                <View style={styles.wsFooterItem}>
                    <Icon name="check-circle-outline" size={14} color={theme.colors.subText} />
                    <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>
                        {completedTasks}/{tasksList.length} {t('repair_tasks_short')}
                    </Text>
                </View>
                <View style={styles.wsFooterItem}>
                    <Icon name="package-variant" size={14} color={theme.colors.subText} />
                    <Text style={[styles.wsFooterText, { color: theme.colors.subText }]}>{partsList.length} {t('repair_parts_short')}</Text>
                </View>
                {totalParts > 0 && (
                    <Text style={[styles.wsTotalText, { color: theme.colors.success }]}>
                        {totalParts.toLocaleString('hu-HU')} Ft
                    </Text>
                )}
            </View>
        </TouchableOpacity>
    );
});

const WorkSheetListScreen = () => {
    const { t } = useTranslation();
    const { theme } = usePremiumLegacyTheme();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const { worksheets, loaded, syncing, errors, worksheetsVersion } = useWorkSheets();
    const topInset = useTopInset();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('active');
    const [isNewModalVisible, setIsNewModalVisible] = useState(false);

    const allSheets = worksheets;

    // First paint: the newest rows of the already-loaded working set, until page 1 answers.
    const seed = useMemo(
        () => allSheets.filter(ws => matchesFilter(ws, 'active')).sort(byNewestFirst).slice(0, WORK_ORDER_PAGE_SIZE),
        // Only the very first render needs it; later renders read the hook's own rows.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const fetchPage = useCallback(
        ({ page, limit, search, skipCache }) => fetchWorkOrderPage({ page, limit, search, skipCache, filter: activeFilter }),
        [activeFilter]
    );

    const {
        items: filteredSheets,
        loading: listLoading,
        loadingMore,
        refreshing,
        error: listError,
        loadMore,
        refresh,
    } = usePagedServerList({
        fetchPage,
        search: searchQuery,
        queryKey: activeFilter,
        limit: WORK_ORDER_PAGE_SIZE,
        refreshKey: worksheetsVersion,
        seed,
    });

    // Per-pill counters come from one cheap aggregate (summary=true), never from the loaded rows.
    const [serverCounts, setServerCounts] = useState(null);
    const countsTimerRef = useRef(null);
    useEffect(() => {
        let active = true;
        clearTimeout(countsTimerRef.current);
        countsTimerRef.current = setTimeout(() => {
            fetchWorkOrderCounts()
                .then((counts) => { if (active) setServerCounts(counts); })
                .catch(() => {});
        }, 400);

        return () => {
            active = false;
            clearTimeout(countsTimerRef.current);
        };
    }, [worksheetsVersion]);

    const onRefresh = useCallback(async () => {
        await refresh();
        fetchWorkOrderCounts({ skipCache: true }).then(setServerCounts).catch(() => {});
    }, [refresh]);

    // Until the aggregate arrives, show counts from the in-memory working set.
    const fallbackCounts = useMemo(() => (serverCounts ? null : countByFilter(allSheets)), [serverCounts, allSheets]);

    const getFilterLabel = useCallback((filter) => {
        const labels = {
            all: t('repair_filter_all'),
            active: t('repair_filter_active'),
            pending: t('repair_status_pending'),
            in_progress: t('repair_status_in_progress'),
            completed: t('repair_status_completed'),
            invoiced: t('repair_status_invoiced'),
        };
        return labels[filter] || filter;
    }, [t]);

    const getFilterCount = useCallback((filter) => (serverCounts || fallbackCounts || {})[filter] || 0, [serverCounts, fallbackCounts]);

    const handleSheetPress = useCallback((worksheet) => {
        navigation.navigate('WorkSheetDetailScreen', { worksheet });
    }, [navigation]);

    const handleSaveNewWorksheet = useCallback(async (worksheetData) => {
        // The modal already persisted the work order via the composite API.
        // Just navigate to the detail screen with the returned data.
        setIsNewModalVisible(false);
        if (worksheetData) {
            navigation.navigate('WorkSheetDetailScreen', {
                isNew: false,
                worksheet: worksheetData,
            });
        }
    }, [navigation]);

    // Stable keyExtractor identity (byte-identical key: item._id), matching the
    // other blueprint list screens so it isn't reallocated on every render.
    const keyExtractor = useCallback((item) => item._id, []);

    const renderWorkSheet = useCallback(({ item }) => (
        <WorkSheetCard item={item} onPress={handleSheetPress} theme={theme} m={m} t={t} />
    ), [handleSheetPress, t, theme, m]);

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            {/* Header */}
            <View style={[styles.header, {paddingTop: topInset + 12}]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={{
                        width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center',
                        backgroundColor: theme.colors.card, borderWidth: 1, borderColor: theme.colors.border,
                    }}>
                        <Icon name="chevron-left" size={26} color={theme.colors.text} />
                    </TouchableOpacity>
                    <Text style={[styles.headerTitle, { color: theme.colors.text }]}>{t('repair_worksheets')}</Text>
                </View>
                <TouchableOpacity
                    style={[styles.newButton, { backgroundColor: theme.colors.primary }]}
                    onPress={() => setIsNewModalVisible(true)}
                    activeOpacity={0.7}
                >
                    <Icon name="plus" size={20} color="#fff" />
                    <Text style={styles.newButtonText}>{t('repair_new')}</Text>
                </TouchableOpacity>
            </View>

            {/* Search */}
            <View style={[styles.searchContainer, {
                backgroundColor: theme.colors.card,
                borderColor: theme.colors.border,
            }]}>
                <Icon name="magnify" size={20} color={theme.colors.subText} style={styles.searchIcon} />
                <TextInput
                    style={[styles.searchInput, { color: theme.colors.text }]}
                    placeholder={t('repair_search_placeholder')}
                    placeholderTextColor={theme.colors.placeholder}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                />
                {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setSearchQuery('')}>
                        <Icon name="close-circle" size={18} color={theme.colors.subText} />
                    </TouchableOpacity>
                )}
            </View>

            {/* Status Filters */}
            <View style={styles.filtersSection}>
                <PillTabs
                    scroll
                    variant="solid"
                    options={STATUS_FILTERS.map((filter) => ({ value: filter, label: `${getFilterLabel(filter)} (${getFilterCount(filter)})` }))}
                    value={activeFilter}
                    onChange={setActiveFilter}
                />
            </View>

            {/* Work Sheet List */}
            {(!loaded || listLoading) && filteredSheets.length === 0 ? (
                <BoboLoading />
            ) : (
                <FlatList
                    data={filteredSheets}
                    renderItem={renderWorkSheet}
                    keyExtractor={keyExtractor}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                    initialNumToRender={8}
                    maxToRenderPerBatch={6}
                    updateCellsBatchingPeriod={60}
                    windowSize={7}
                    removeClippedSubviews={Platform.OS === 'android'}
                    onEndReached={loadMore}
                    onEndReachedThreshold={0.6}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing || syncing}
                            onRefresh={onRefresh}
                            tintColor={theme.colors.primary}
                            colors={[theme.colors.primary]}
                        />
                    }
                    ListFooterComponent={loadingMore ? (
                        <ActivityIndicator style={styles.listFooterSpinner} color={theme.colors.primary} />
                    ) : null}
                    ListEmptyComponent={
                        <ServiceEmptyState
                            theme={theme}
                            icon="file-search-outline"
                            title={t('repair_no_worksheets')}
                            subtitle={listError || errors?.worksheets || t('repair_add_new_worksheet') || ''}
                            actionLabel={t('repair_new')}
                            onAction={() => setIsNewModalVisible(true)}
                        />
                    }
                />
            )}

            <NewWorkSheetModal
                visible={isNewModalVisible}
                onClose={() => setIsNewModalVisible(false)}
                onSave={handleSaveNewWorksheet}
                theme={theme}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        // paddingTop applied dynamically via useTopInset()
        paddingBottom: 12,
    },
    headerTitle: {
        fontSize: 28,
        fontWeight: '800',
        letterSpacing: -0.6,
    },
    newButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 999,
        gap: 6,
    },
    newButtonText: {
        color: '#fff',
        fontWeight: '600',
        fontSize: 14,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 999,
        marginHorizontal: 16,
        paddingHorizontal: 16,
        marginBottom: 12,
        borderWidth: 1,
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        height: 44,
        fontSize: 15,
    },
    filtersSection: {
        zIndex: 10,
        marginBottom: 12,
        paddingHorizontal: 12,
    },
    listContent: {
        paddingHorizontal: 16,
        paddingTop: 2,
        paddingBottom: 80,
    },
    wsCard: {
        borderRadius: 24,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
    },
    wsCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    wsIdContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    wsId: {
        fontSize: 13,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    },
    statusBadgeText: {
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    wsCardBody: {
        marginBottom: 12,
    },
    wsCustomer: {
        fontSize: 17,
        fontWeight: '600',
        marginBottom: 6,
    },
    wsCarRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 4,
    },
    wsCarText: {
        fontSize: 13,
    },
    wsPlateBadge: {
        paddingHorizontal: 10,
        paddingVertical: 2,
        borderRadius: 999,
        borderWidth: 1,
    },
    wsPlateText: {
        fontSize: 12,
        fontWeight: '700',
        letterSpacing: 1,
    },
    wsJobText: {
        fontSize: 13,
        marginTop: 4,
    },
    wsCardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        paddingTop: 10,
        borderTopWidth: 1,
    },
    wsFooterItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    wsFooterText: {
        fontSize: 12,
        fontWeight: '500',
    },
    wsTotalText: {
        fontSize: 13,
        fontWeight: '700',
        marginLeft: 'auto',
    },
    listFooterSpinner: {
        paddingVertical: 16,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 60,
        gap: 12,
    },
    emptyText: {
        fontSize: 15,
        fontWeight: '500',
    },
});

export default WorkSheetListScreen;
